import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, BookOpen, Lightbulb, MessageCircle, Plus, History } from 'lucide-react';
import { 
  processAIChat, 
  createChatSession, 
  getChatHistory, 
  getUserChatSessions 
} from '../../services/aiChatService';
import { supabase } from '../../lib/supabase';

const AIChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [user, setUser] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const messagesEndRef = useRef(null);

  // Sample smart cities questions
  const sampleQuestions = [
    "What are the best practices for implementing smart traffic management systems?",
    "How can cities improve energy efficiency through smart grid technology?",
    "What are the key components of a successful smart parking system?",
    "How do IoT sensors help in waste management optimization?",
    "What are the privacy considerations for smart city data collection?",
  ];

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (user) {
      loadChatSessions();
    }
  }, [user]);

  const getCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (!error && user) {
        setUser(user);
      }
    } catch (error) {
      console.error('Error getting user:', error);
    }
  };

  const loadChatSessions = async () => {
    if (!user?.id) return;
    
    try {
      const sessions = await getUserChatSessions(user?.id);
      setChatSessions(sessions || []);
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startNewChat = async () => {
    if (!user?.id) {
      alert('Please sign in to start a chat session');
      return;
    }

    try {
      const session = await createChatSession(user?.id, 'Smart Cities Chat');
      setCurrentSession(session);
      setMessages([]);
      setShowSidebar(false);
      await loadChatSessions();
    } catch (error) {
      console.error('Error creating new chat:', error);
    }
  };

  const loadChatSession = async (sessionId) => {
    try {
      const history = await getChatHistory(sessionId);
      const formattedMessages = history?.map(msg => ({
        id: msg?.id,
        role: msg?.role,
        content: msg?.content,
        timestamp: msg?.created_at,
        sources: msg?.referenced_knowledge_ids || [],
      })) || [];
      
      setMessages(formattedMessages);
      setCurrentSession({ id: sessionId });
      setShowSidebar(false);
    } catch (error) {
      console.error('Error loading chat session:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || loading) return;
    
    if (!currentSession) {
      await startNewChat();
      if (!currentSession) return;
    }

    const userMessage = inputMessage?.trim();
    setInputMessage('');
    setLoading(true);

    // Add user message immediately
    const userMsgId = Date.now();
    setMessages(prev => [...prev, {
      id: userMsgId,
      role: 'user',
      content: userMessage,
      timestamp: new Date()?.toISOString(),
      sources: [],
    }]);

    try {
      const result = await processAIChat(userMessage, currentSession?.id);
      
      // Add AI response
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: result?.response,
        timestamp: new Date()?.toISOString(),
        sources: result?.sources || [],
        hasKnowledgeContext: result?.hasKnowledgeContext,
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()?.toISOString(),
        sources: [],
        hasKnowledgeContext: false,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleSampleQuestion = async (question) => {
    setInputMessage(question);
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-lg transform transition-transform lg:relative lg:translate-x-0 lg:z-0`}>
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Smart Cities AI</h2>
          <button
            onClick={startNewChat}
            className="w-full flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        <div className="p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <History className="w-4 h-4" />
            Recent Chats
          </h3>
          <div className="space-y-2">
            {chatSessions?.length > 0 ? (
              chatSessions?.map((session) => (
                <button
                  key={session?.id}
                  onClick={() => loadChatSession(session?.id)}
                  className={`w-full text-left p-3 rounded-lg hover:bg-gray-100 ${
                    currentSession?.id === session?.id ? 'bg-blue-50 border border-blue-200' : ''
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {session?.session_title}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(session?.created_at)?.toLocaleDateString()}
                  </div>
                </button>
              ))
            ) : (
              <p className="text-sm text-gray-500">No previous chats</p>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSidebar(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Smart Cities AI Assistant</h1>
                  <p className="text-sm text-gray-600">Ask questions about urban planning and smart city technologies</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto">
            {messages?.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Welcome to Smart Cities AI Assistant
                </h2>
                <p className="text-gray-600 mb-6">
                  I can help you with questions about urban planning, smart city technologies, and municipal governance.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                  {sampleQuestions?.slice(0, 4)?.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSampleQuestion(question)}
                      className="p-3 text-left bg-white rounded-lg border hover:border-blue-300 hover:shadow-sm transition-all text-sm"
                    >
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{question}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages?.map((message) => (
                  <div
                    key={message?.id}
                    className={`flex gap-3 ${
                      message?.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message?.role === 'assistant' && (
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                    )}
                    
                    <div className={`max-w-3xl ${
                      message?.role === 'user' ?'bg-blue-600 text-white rounded-lg px-4 py-3' :'bg-white rounded-lg border p-4'
                    }`}>
                      <div className="whitespace-pre-wrap">{message?.content}</div>
                      
                      {/* Knowledge Sources */}
                      {message?.sources?.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <BookOpen className="w-4 h-4" />
                            <span>Sources from Knowledge Base:</span>
                          </div>
                          <div className="space-y-2">
                            {message?.sources?.map((source, index) => (
                              <div key={index} className="text-sm bg-blue-50 rounded p-2">
                                <div className="font-medium text-blue-900">{source?.title}</div>
                                {source?.category && (
                                  <div className="text-blue-600 text-xs">{source?.category}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(message?.timestamp)?.toLocaleTimeString()}
                      </div>
                    </div>

                    {message?.role === 'user' && (
                      <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="bg-white border-t p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about smart cities, urban planning, IoT sensors, or municipal governance..."
                  rows={inputMessage?.split('\n')?.length || 1}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none max-h-32"
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage?.trim() || loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;